import axios, { AxiosResponse } from 'axios';
import { getEnv } from '../config';
import {
  ISimCard,
  ISimListResponse,
  ISimStatus,
  IApiResponse,
  ISimUpdateRequest,
  ISimListParams,
  IOAuthTokenResponse,
  IOAuthTokenRequest,
  ISimOwner,
  IModem,
  IModemListResponse,
  IModemCreateRequest,
  IModemUpdateRequest,
  IModemListParams,
  IModemAssignRequest,
  IModemAssignResponse,
} from '@/types/api';

import {
  IPurchase,
  IPurchaseListResponse,
  IPurchaseCreateRequest,
  IPurchaseUpdateRequest,
  ISale,
  ISaleListResponse,
  ISaleCreateRequest,
  ISaleUpdateRequest,
  IInventoryItem,
  IInventoryListResponse,
  IInventoryCreateRequest,
  IInventoryUpdateRequest,
  IInvoice,
  IInvoiceListResponse,
  IInvoiceCreateRequest,
  IInvoiceUpdateRequest,
  IPSIListParams,
  IVendor,
  IVendorCreateRequest,
  ICustomer,
  ICustomerCreateRequest,
  ICellUpdateRequest,
  IBatchCellUpdateRequest,
  ICategoryInfo,
  IPartCode,
  IPartCodeCreateRequest,
  IPartCodeCellUpdateRequest,
  IFinanceTransaction,
  IFinanceListResponse,
  IFinanceCreateRequest,
  IFinanceUpdateRequest,
  IStockTakeRecord,
  IStockTakeListResponse,
  IStockTakeGenerateRequest,
  IStockTakeUpdateRequest,
  IStockTakeBatchUpdateRequest,
  IStockTakeSession,
  IExpenditure,
  IExpenditureListResponse,
  IExpenditureCreateRequest,
  IExpenditureUpdateRequest,
  IExpenditurePeriodResponse,
} from '@/types/psi';

import {
  ILoginRequest,
  IGoogleLoginRequest,
  IPasscodeVerifyRequest,
  IPasscodeVerifyResponse,
  ILoginResponse,
  IEmployeePublic,
  IEmployeeSalaryInfo,
  ICurrentUserResponse,
} from '@/types/auth';

import {
  IWorkShift,
  IWorkShiftCreateRequest,
  IWorkShiftListResponse,
  IMonthlySummary,
  ISalarySlip,
  IWorkShiftListParams,
  IEmployeeListItem,
} from '@/types/workshift';

import {
  IKanbanTask,
  ITaskCreateRequest,
  ITaskUpdateRequest,
  ITaskMoveRequest,
  ITaskReorderRequest,
  ITaskResponse,
  ITaskListResponse,
  IDriveFile,
  IFileUploadResponse,
  IFileListResponse,
} from '@/types/kanban';

import {
  IOnlineUser,
  IStoredVoiceMessage,
  IVoiceMessageRequest,
  IPendingCountResponse,
} from '@/types/voicemessaging';

import {
  IProject,
  IProjectPhase,
  IProjectTask,
  IProjectCreateRequest,
  IProjectUpdateRequest,
  IPhaseCreateRequest,
  IPhaseUpdateRequest,
  ITaskCreateRequest as IProjectTaskCreateRequest,
  ITaskUpdateRequest as IProjectTaskUpdateRequest,
  ITaskMoveRequest as IProjectTaskMoveRequest,
  IReorderRequest,
  IProjectListResponse,
  IProjectDetailResponse,
  IPhaseListResponse,
  ITaskListResponse as IProjectTaskListResponse,
  IDependencyCheckResponse,
} from '@/types/project';

// Create axios instance with default config
const api = axios.create({
  baseURL: getEnv('VITE_API_URL') || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export class ApiService {
  // Health Check
  static async checkHealth(): Promise<IApiResponse> {
    try {
      const response: AxiosResponse<IApiResponse> = await api.get('/health');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Health check failed');
    }
  }

  // OAuth Authentication
  static async getOAuthToken(credentials: IOAuthTokenRequest): Promise<IOAuthTokenResponse> {
    try {
      const response: AxiosResponse<IOAuthTokenResponse> = await api.post('/v1/oauth/token', credentials);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Authentication failed');
    }
  }

  // Owner Management
  static async getOwner(iccid: string): Promise<ISimOwner> {
    try {
      const response: AxiosResponse<ISimOwner> = await api.get(`/v1/sims/${iccid}/owner`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch owner information');
    }
  }

  // SIM Card Management
  static async getSimCards(params: ISimListParams = {}): Promise<ISimListResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) queryParams.append('page', params.page.toString());
      if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
      if (params.q) queryParams.append('q', params.q);
      if (params.sort) queryParams.append('sort', params.sort);

      const response: AxiosResponse<ISimListResponse> = await api.get(
        `/v1/sims${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch SIM cards');
    }
  }

  static async getSimCard(iccid: string): Promise<ISimCard> {
    try {
      const response: AxiosResponse<ISimCard> = await api.get(`/v1/sims/${iccid}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch SIM card');
    }
  }

  static async updateSimCard(iccid: string, data: ISimUpdateRequest): Promise<IApiResponse> {
    try {
      const response: AxiosResponse<IApiResponse> = await api.put(`/v1/sims/${iccid}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update SIM card');
    }
  }

  static async getSimStatus(iccid: string): Promise<ISimStatus> {
    try {
      const response: AxiosResponse<ISimStatus> = await api.get(`/v1/sims/${iccid}/status`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch SIM status');
    }
  }

  static async getSimConnectivity(
    iccid: string,
    params: { subscriber?: boolean; ussd?: boolean } = {}
  ): Promise<any> {
    try {
      const queryParams = new URLSearchParams();

      if (params.subscriber !== undefined) queryParams.append('subscriber', params.subscriber.toString());
      if (params.ussd !== undefined) queryParams.append('ussd', params.ussd.toString());

      const response: AxiosResponse<any> = await api.get(
        `/v1/sims/${iccid}/connectivity${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch SIM connectivity');
    }
  }

  // Modem Management
  static async getModems(params: IModemListParams = {}): Promise<IModemListResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) queryParams.append('page', params.page.toString());
      if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
      if (params.q) queryParams.append('q', params.q);
      if (params.sort) queryParams.append('sort', params.sort);

      const response: AxiosResponse<IModemListResponse> = await api.get(
        `/v1/modems${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch modems');
    }
  }

  static async getModem(modemId: string): Promise<IModem> {
    try {
      const response: AxiosResponse<IModem> = await api.get(`/v1/modems/${modemId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch modem');
    }
  }

  static async createModem(data: IModemCreateRequest): Promise<IModem> {
    try {
      const response: AxiosResponse<IModem> = await api.post('/v1/modems', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create modem');
    }
  }

  static async updateModem(modemId: string, data: IModemUpdateRequest): Promise<IModem> {
    try {
      const response: AxiosResponse<IModem> = await api.put(`/v1/modems/${modemId}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update modem');
    }
  }

  static async deleteModem(modemId: string): Promise<IApiResponse> {
    try {
      const response: AxiosResponse<IApiResponse> = await api.delete(`/v1/modems/${modemId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete modem');
    }
  }

  // Modem Assignment (v2 - sends to customer's gRPC server)
  static async assignModem(data: IModemAssignRequest): Promise<IModemAssignResponse> {
    try {
      const response: AxiosResponse<IModemAssignResponse> = await api.post('/v1/modems/assign', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to assign modem');
    }
  }

  // ============================================================================
  // PSI - Purchases
  // ============================================================================

  static async getPurchases(params: IPSIListParams = {}): Promise<IPurchaseListResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
      if (params.q) queryParams.append('q', params.q);

      const response: AxiosResponse<IPurchaseListResponse> = await api.get(
        `/v1/psi/purchases${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch purchases');
    }
  }

  static async getPurchase(id: string): Promise<IPurchase> {
    try {
      const response: AxiosResponse<IPurchase> = await api.get(`/v1/psi/purchases/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch purchase');
    }
  }

  static async createPurchase(data: IPurchaseCreateRequest): Promise<IPurchase> {
    try {
      const response: AxiosResponse<IPurchase> = await api.post('/v1/psi/purchases', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create purchase');
    }
  }

  static async updatePurchase(id: string, data: IPurchaseUpdateRequest): Promise<IPurchase> {
    try {
      const response: AxiosResponse<IPurchase> = await api.put(`/v1/psi/purchases/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update purchase');
    }
  }

  static async deletePurchase(id: string): Promise<IApiResponse> {
    try {
      const response: AxiosResponse<IApiResponse> = await api.delete(`/v1/psi/purchases/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete purchase');
    }
  }

  // ============================================================================
  // PSI - Sales
  // ============================================================================

  static async getSales(params: IPSIListParams = {}): Promise<ISaleListResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
      if (params.q) queryParams.append('q', params.q);

      const response: AxiosResponse<ISaleListResponse> = await api.get(
        `/v1/psi/sales${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch sales');
    }
  }

  static async getSale(id: string): Promise<ISale> {
    try {
      const response: AxiosResponse<ISale> = await api.get(`/v1/psi/sales/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch sale');
    }
  }

  static async createSale(data: ISaleCreateRequest): Promise<ISale> {
    try {
      const response: AxiosResponse<ISale> = await api.post('/v1/psi/sales', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create sale');
    }
  }

  static async updateSale(id: string, data: ISaleUpdateRequest): Promise<ISale> {
    try {
      const response: AxiosResponse<ISale> = await api.put(`/v1/psi/sales/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update sale');
    }
  }

  static async deleteSale(id: string): Promise<IApiResponse> {
    try {
      const response: AxiosResponse<IApiResponse> = await api.delete(`/v1/psi/sales/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete sale');
    }
  }

  // ============================================================================
  // PSI - Inventory
  // ============================================================================

  static async getInventoryItems(params: IPSIListParams = {}): Promise<IInventoryListResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
      if (params.q) queryParams.append('q', params.q);

      const response: AxiosResponse<IInventoryListResponse> = await api.get(
        `/v1/psi/inventory${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch inventory');
    }
  }

  static async getInventoryItem(sku: string): Promise<IInventoryItem> {
    try {
      const response: AxiosResponse<IInventoryItem> = await api.get(`/v1/psi/inventory/${sku}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch inventory item');
    }
  }

  static async createInventoryItem(data: IInventoryCreateRequest): Promise<IInventoryItem> {
    try {
      const response: AxiosResponse<IInventoryItem> = await api.post('/v1/psi/inventory', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create inventory item');
    }
  }

  static async updateInventoryItem(sku: string, data: IInventoryUpdateRequest): Promise<IInventoryItem> {
    try {
      const response: AxiosResponse<IInventoryItem> = await api.put(`/v1/psi/inventory/${sku}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update inventory item');
    }
  }

  static async deleteInventoryItem(sku: string): Promise<IApiResponse> {
    try {
      const response: AxiosResponse<IApiResponse> = await api.delete(`/v1/psi/inventory/${sku}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete inventory item');
    }
  }

  // ============================================================================
  // PSI - Stock Taking
  // ============================================================================

  static async generateStockTake(data: IStockTakeGenerateRequest): Promise<IStockTakeListResponse> {
    try {
      const response: AxiosResponse<IStockTakeListResponse> = await api.post('/v1/psi/stocktake/generate', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to generate stock take');
    }
  }

  static async getStockTakeSessions(): Promise<IStockTakeSession[]> {
    try {
      const response: AxiosResponse<IStockTakeSession[]> = await api.get('/v1/psi/stocktake/sessions');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch stock take sessions');
    }
  }

  static async getStockTakeRecords(year: number, month: number): Promise<IStockTakeListResponse> {
    try {
      const response: AxiosResponse<IStockTakeListResponse> = await api.get(`/v1/psi/stocktake/${year}/${month}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch stock take records');
    }
  }

  static async updateStockTakeRecord(
    year: number,
    month: number,
    id: string,
    data: IStockTakeUpdateRequest
  ): Promise<IStockTakeRecord> {
    try {
      const response: AxiosResponse<IStockTakeRecord> = await api.put(
        `/v1/psi/stocktake/${year}/${month}/${id}`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update stock take record');
    }
  }

  static async batchUpdateStockTakeRecords(
    year: number,
    month: number,
    data: IStockTakeBatchUpdateRequest
  ): Promise<IStockTakeListResponse> {
    try {
      const response: AxiosResponse<IStockTakeListResponse> = await api.put(
        `/v1/psi/stocktake/${year}/${month}/batch`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to batch update stock take records');
    }
  }

  // ============================================================================
  // PSI - Invoices
  // ============================================================================

  static async getInvoices(params: IPSIListParams = {}): Promise<IInvoiceListResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
      if (params.q) queryParams.append('q', params.q);

      const response: AxiosResponse<IInvoiceListResponse> = await api.get(
        `/v1/psi/invoices${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch invoices');
    }
  }

  static async getInvoice(invoiceNo: string): Promise<IInvoice> {
    try {
      const response: AxiosResponse<IInvoice> = await api.get(`/v1/psi/invoices/${invoiceNo}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch invoice');
    }
  }

  static async createInvoice(data: IInvoiceCreateRequest): Promise<IInvoice> {
    try {
      const response: AxiosResponse<IInvoice> = await api.post('/v1/psi/invoices', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create invoice');
    }
  }

  static async updateInvoice(invoiceNo: string, data: IInvoiceUpdateRequest): Promise<IInvoice> {
    try {
      const response: AxiosResponse<IInvoice> = await api.put(`/v1/psi/invoices/${invoiceNo}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update invoice');
    }
  }

  static async deleteInvoice(invoiceNo: string): Promise<IApiResponse> {
    try {
      const response: AxiosResponse<IApiResponse> = await api.delete(`/v1/psi/invoices/${invoiceNo}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete invoice');
    }
  }

  // ============================================================================
  // PSI - Expenditures (Company Expenses)
  // ============================================================================

  static async getExpenditures(params: IPSIListParams = {}): Promise<IExpenditureListResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
      if (params.q) queryParams.append('q', params.q);

      const response: AxiosResponse<IExpenditureListResponse> = await api.get(
        `/v1/psi/expenditures${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch expenditures');
    }
  }

  static async getExpenditure(id: string): Promise<IExpenditure> {
    try {
      const response: AxiosResponse<IExpenditure> = await api.get(`/v1/psi/expenditures/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch expenditure');
    }
  }

  static async createExpenditure(data: IExpenditureCreateRequest): Promise<IExpenditure> {
    try {
      const response: AxiosResponse<IExpenditure> = await api.post('/v1/psi/expenditures', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create expenditure');
    }
  }

  static async updateExpenditure(id: string, data: IExpenditureUpdateRequest): Promise<IExpenditure> {
    try {
      const response: AxiosResponse<IExpenditure> = await api.put(`/v1/psi/expenditures/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update expenditure');
    }
  }

  static async deleteExpenditure(id: string): Promise<IApiResponse> {
    try {
      const response: AxiosResponse<IApiResponse> = await api.delete(`/v1/psi/expenditures/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete expenditure');
    }
  }

  static async getExpenditureSummary(year: number, period: string = 'monthly'): Promise<IExpenditurePeriodResponse> {
    try {
      const response: AxiosResponse<IExpenditurePeriodResponse> = await api.get(
        `/v1/psi/expenditures/summary?year=${year}&period=${period}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch expenditure summary');
    }
  }

  // ============================================================================
  // Authentication
  // ============================================================================

  static async login(credentials: ILoginRequest): Promise<ILoginResponse> {
    try {
      const response: AxiosResponse<ILoginResponse> = await api.post('/v1/auth/login', credentials);
      // Store token in localStorage
      if (response.data.token) {
        localStorage.setItem('auth_token', response.data.token);
      }
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Login failed');
    }
  }

  static async getCurrentUser(): Promise<ICurrentUserResponse> {
    try {
      const response: AxiosResponse<ICurrentUserResponse> = await api.get('/v1/auth/me');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to get current user');
    }
  }

  static async getSalaryInfo(): Promise<IEmployeeSalaryInfo> {
    try {
      const response: AxiosResponse<IEmployeeSalaryInfo> = await api.get('/v1/auth/salary-info');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to get salary info');
    }
  }

  static logout(): void {
    localStorage.removeItem('auth_token');
  }

  static async loginWithGoogle(request: IGoogleLoginRequest): Promise<ILoginResponse> {
    try {
      const response: AxiosResponse<ILoginResponse> = await api.post('/v1/auth/google', request);
      if (response.data.token) {
        localStorage.setItem('auth_token', response.data.token);
      }
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data || 'Google login failed');
    }
  }

  static async verifyPasscode(request: IPasscodeVerifyRequest): Promise<IPasscodeVerifyResponse> {
    try {
      const response: AxiosResponse<IPasscodeVerifyResponse> = await api.post('/v1/auth/verify-passcode', request);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data || 'Passcode verification failed');
    }
  }

  // ============================================================================
  // Work Shifts
  // ============================================================================

  static async getWorkShifts(params: IWorkShiftListParams & { user?: string }): Promise<IWorkShiftListResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('year', params.year.toString());
      queryParams.append('month', params.month.toString());
      if (params.user) {
        queryParams.append('user', params.user);
      }

      const response: AxiosResponse<IWorkShiftListResponse> = await api.get(
        `/v1/workshifts?${queryParams.toString()}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch work shifts');
    }
  }

  static async createWorkShift(data: IWorkShiftCreateRequest): Promise<IWorkShift> {
    try {
      const response: AxiosResponse<IWorkShift> = await api.post('/v1/workshifts', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create work shift');
    }
  }

  static async updateWorkShift(date: string, data: IWorkShiftCreateRequest): Promise<IWorkShift> {
    try {
      const response: AxiosResponse<IWorkShift> = await api.put(`/v1/workshifts/${date}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update work shift');
    }
  }

  static async deleteWorkShift(date: string): Promise<IApiResponse> {
    try {
      const response: AxiosResponse<IApiResponse> = await api.delete(`/v1/workshifts/${date}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete work shift');
    }
  }

  static async getMonthlySummary(params: IWorkShiftListParams & { user?: string }): Promise<IMonthlySummary> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('year', params.year.toString());
      queryParams.append('month', params.month.toString());
      if (params.user) {
        queryParams.append('user', params.user);
      }

      const response: AxiosResponse<IMonthlySummary> = await api.get(
        `/v1/workshifts/summary?${queryParams.toString()}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch monthly summary');
    }
  }

  static async getSalarySlip(params: IWorkShiftListParams & { user?: string }): Promise<ISalarySlip> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('year', params.year.toString());
      queryParams.append('month', params.month.toString());
      if (params.user) {
        queryParams.append('user', params.user);
      }

      const response: AxiosResponse<ISalarySlip> = await api.get(
        `/v1/salary-slip?${queryParams.toString()}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch salary slip');
    }
  }

  static async getEmployees(): Promise<IEmployeeListItem[]> {
    try {
      const response: AxiosResponse<IEmployeeListItem[]> = await api.get('/v1/employees');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch employees');
    }
  }

  static async updateWorkShiftNotes(user: string, date: string, notes: string): Promise<IApiResponse> {
    try {
      const response: AxiosResponse<IApiResponse> = await api.patch(
        `/v1/workshifts/${user}/${date}/notes`,
        { notes }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update work shift notes');
    }
  }

  // ============================================================================
  // Vendors
  // ============================================================================

  static async getVendors(): Promise<IVendor[]> {
    try {
      const response: AxiosResponse<IVendor[]> = await api.get('/v1/vendors');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch vendors');
    }
  }

  static async createVendor(data: IVendorCreateRequest): Promise<{ status: string }> {
    try {
      const response: AxiosResponse<{ status: string }> = await api.post('/v1/vendors', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create vendor');
    }
  }

  static async updateVendorCell(data: ICellUpdateRequest): Promise<{ status: string }> {
    try {
      const response: AxiosResponse<{ status: string }> = await api.patch('/v1/vendors/cell', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update vendor cell');
    }
  }

  static async batchUpdateVendor(data: IBatchCellUpdateRequest): Promise<{ status: string }> {
    try {
      const response: AxiosResponse<{ status: string }> = await api.patch('/v1/vendors/batch', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to batch update vendor');
    }
  }

  static async deleteVendor(rowIndex: number): Promise<{ status: string }> {
    try {
      const response: AxiosResponse<{ status: string }> = await api.delete(`/v1/vendors/${rowIndex}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete vendor');
    }
  }

  // ============================================================================
  // Customers
  // ============================================================================

  static async getCustomers(): Promise<ICustomer[]> {
    try {
      const response: AxiosResponse<ICustomer[]> = await api.get('/v1/customers');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch customers');
    }
  }

  static async createCustomer(data: ICustomerCreateRequest): Promise<{ status: string }> {
    try {
      const response: AxiosResponse<{ status: string }> = await api.post('/v1/customers', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create customer');
    }
  }

  static async updateCustomerCell(data: ICellUpdateRequest): Promise<{ status: string }> {
    try {
      const response: AxiosResponse<{ status: string }> = await api.patch('/v1/customers/cell', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update customer cell');
    }
  }

  static async batchUpdateCustomer(data: IBatchCellUpdateRequest): Promise<{ status: string }> {
    try {
      const response: AxiosResponse<{ status: string }> = await api.patch('/v1/customers/batch', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to batch update customer');
    }
  }

  static async deleteCustomer(rowIndex: number): Promise<{ status: string }> {
    try {
      const response: AxiosResponse<{ status: string }> = await api.delete(`/v1/customers/${rowIndex}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete customer');
    }
  }

  // ============================================================================
  // Part Codes
  // ============================================================================

  static async getPartCodeCategories(): Promise<ICategoryInfo[]> {
    try {
      const response: AxiosResponse<ICategoryInfo[]> = await api.get('/v1/partcodes/categories');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch part code categories');
    }
  }

  static async getPartCodes(categoryId: string): Promise<IPartCode[]> {
    try {
      const response: AxiosResponse<IPartCode[]> = await api.get(`/v1/partcodes/${categoryId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch part codes');
    }
  }

  static async createPartCode(categoryId: string, data: IPartCodeCreateRequest): Promise<{ status: string }> {
    try {
      const response: AxiosResponse<{ status: string }> = await api.post(`/v1/partcodes/${categoryId}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create part code');
    }
  }

  // Parts Master (Firestore — Phase 1a/1b)
  static async createPart(data: {
    part_code?: string;
    description: string;
    part_type: string;
    package_size?: string;
    category: string;
    subcategory?: string;
    specification?: string;
    brand?: string;
    manufacturer_pn?: string;
    digikey_pn?: string;
    datasheet_url?: string;
    datasheet_file_id?: string;
  }): Promise<{ id: string; part_code: string }> {
    try {
      const response = await api.post('/v1/parts', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create part');
    }
  }

  static async updatePartCodeCell(categoryId: string, data: IPartCodeCellUpdateRequest): Promise<{ status: string }> {
    try {
      const response: AxiosResponse<{ status: string }> = await api.patch(`/v1/partcodes/${categoryId}/cell`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update part code cell');
    }
  }

  static async deletePartCode(categoryId: string, rowIndex: number): Promise<{ status: string }> {
    try {
      const response: AxiosResponse<{ status: string }> = await api.delete(`/v1/partcodes/${categoryId}/${rowIndex}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete part code');
    }
  }

  // ============================================================================
  // Finance (Mock Data - Cash-in / Cash-out)
  // ============================================================================

  private static mockFinanceTransactions: IFinanceTransaction[] = [
    {
      transaction_id: 'FIN-001',
      type: 'cash_in',
      date: '2024-12-20',
      amount: 150000,
      category: '銷售收入',
      description: '客戶訂單 ORD-2024-001',
      reference: 'INV-2024-001',
      payment_method: '銀行轉帳',
      status: 'completed',
      created_by: 'admin',
      notes: '',
    },
    {
      transaction_id: 'FIN-002',
      type: 'cash_out',
      date: '2024-12-19',
      amount: 45000,
      category: '採購支出',
      description: '供應商付款 - ABC Electronics',
      reference: 'PO-2024-015',
      payment_method: '銀行轉帳',
      status: 'completed',
      created_by: 'admin',
      notes: '',
    },
    {
      transaction_id: 'FIN-003',
      type: 'cash_out',
      date: '2024-12-18',
      amount: 85000,
      category: '薪資支出',
      description: '12月份員工薪資',
      reference: 'PAY-2024-12',
      payment_method: '銀行轉帳',
      status: 'completed',
      created_by: 'admin',
      notes: '包含獎金',
    },
    {
      transaction_id: 'FIN-004',
      type: 'cash_in',
      date: '2024-12-17',
      amount: 280000,
      category: '銷售收入',
      description: '客戶訂單 ORD-2024-002',
      reference: 'INV-2024-002',
      payment_method: '信用卡',
      status: 'completed',
      created_by: 'admin',
      notes: '',
    },
    {
      transaction_id: 'FIN-005',
      type: 'cash_out',
      date: '2024-12-16',
      amount: 25000,
      category: '租金支出',
      description: '12月份辦公室租金',
      reference: 'RENT-2024-12',
      payment_method: '銀行轉帳',
      status: 'completed',
      created_by: 'admin',
      notes: '',
    },
    {
      transaction_id: 'FIN-006',
      type: 'cash_in',
      date: '2024-12-15',
      amount: 50000,
      category: '服務收入',
      description: '技術支援服務',
      reference: 'SVC-2024-003',
      payment_method: '現金',
      status: 'pending',
      created_by: 'admin',
      notes: '待確認到帳',
    },
    {
      transaction_id: 'FIN-007',
      type: 'cash_out',
      date: '2024-12-14',
      amount: 8500,
      category: '水電費',
      description: '11月份水電費',
      reference: 'UTIL-2024-11',
      payment_method: '銀行轉帳',
      status: 'completed',
      created_by: 'admin',
      notes: '',
    },
    {
      transaction_id: 'FIN-008',
      type: 'cash_out',
      date: '2024-12-13',
      amount: 3200,
      category: '辦公用品',
      description: '辦公室文具採購',
      reference: 'OFC-2024-008',
      payment_method: '現金',
      status: 'completed',
      created_by: 'admin',
      notes: '',
    },
  ];

  static async getFinanceTransactions(params: IPSIListParams = {}): Promise<IFinanceListResponse> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300));

    let transactions = [...this.mockFinanceTransactions];

    // Filter by search query
    if (params.q) {
      const query = params.q.toLowerCase();
      transactions = transactions.filter(t =>
        t.description.toLowerCase().includes(query) ||
        t.category.toLowerCase().includes(query) ||
        t.reference.toLowerCase().includes(query) ||
        t.transaction_id.toLowerCase().includes(query)
      );
    }

    // Calculate summary
    const total_cash_in = transactions
      .filter(t => t.type === 'cash_in' && t.status === 'completed')
      .reduce((sum, t) => sum + t.amount, 0);
    const total_cash_out = transactions
      .filter(t => t.type === 'cash_out' && t.status === 'completed')
      .reduce((sum, t) => sum + t.amount, 0);

    // Pagination
    const page = params.page || 1;
    const pageSize = params.pageSize || 25;
    const total_count = transactions.length;
    const total_pages = Math.ceil(total_count / pageSize);
    const startIndex = (page - 1) * pageSize;
    const paginatedTransactions = transactions.slice(startIndex, startIndex + pageSize);

    return {
      transactions: paginatedTransactions,
      page,
      page_size: pageSize,
      total_count,
      total_pages,
      summary: {
        total_cash_in,
        total_cash_out,
        net_balance: total_cash_in - total_cash_out,
      },
    };
  }

  static async getFinanceTransaction(id: string): Promise<IFinanceTransaction> {
    await new Promise(resolve => setTimeout(resolve, 200));
    const transaction = this.mockFinanceTransactions.find(t => t.transaction_id === id);
    if (!transaction) {
      throw new Error('Transaction not found');
    }
    return transaction;
  }

  static async createFinanceTransaction(data: IFinanceCreateRequest): Promise<IFinanceTransaction> {
    await new Promise(resolve => setTimeout(resolve, 300));

    const newId = `FIN-${String(this.mockFinanceTransactions.length + 1).padStart(3, '0')}`;
    const newTransaction: IFinanceTransaction = {
      transaction_id: newId,
      type: data.type,
      date: data.date,
      amount: data.amount,
      category: data.category,
      description: data.description,
      reference: data.reference || '',
      payment_method: data.payment_method || '現金',
      status: (data.status as IFinanceTransaction['status']) || 'pending',
      created_by: 'admin',
      notes: data.notes || '',
    };

    this.mockFinanceTransactions.unshift(newTransaction);
    return newTransaction;
  }

  static async updateFinanceTransaction(id: string, data: IFinanceUpdateRequest): Promise<IFinanceTransaction> {
    await new Promise(resolve => setTimeout(resolve, 300));

    const index = this.mockFinanceTransactions.findIndex(t => t.transaction_id === id);
    if (index === -1) {
      throw new Error('Transaction not found');
    }

    this.mockFinanceTransactions[index] = {
      ...this.mockFinanceTransactions[index],
      ...data,
      status: (data.status as IFinanceTransaction['status']) || this.mockFinanceTransactions[index].status,
    };

    return this.mockFinanceTransactions[index];
  }

  static async deleteFinanceTransaction(id: string): Promise<{ status: string }> {
    await new Promise(resolve => setTimeout(resolve, 200));

    const index = this.mockFinanceTransactions.findIndex(t => t.transaction_id === id);
    if (index === -1) {
      throw new Error('Transaction not found');
    }

    this.mockFinanceTransactions.splice(index, 1);
    return { status: 'deleted' };
  }

  // ============================================================================
  // Kanban Board
  // ============================================================================

  // Helper to transform API task (string deps) to frontend task (array deps)
  private static transformTask(task: any): IKanbanTask {
    return {
      ...task,
      prior_deps: task.prior_deps ? task.prior_deps.split(',').filter((s: string) => s.trim()) : [],
      post_deps: task.post_deps ? task.post_deps.split(',').filter((s: string) => s.trim()) : [],
    };
  }

  static async getKanbanTasks(): Promise<ITaskListResponse> {
    try {
      const response: AxiosResponse<any> = await api.get('/v1/kanban/tasks');
      return {
        tasks: (response.data.tasks || []).map(this.transformTask),
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch Kanban tasks');
    }
  }

  static async getKanbanTask(id: string): Promise<ITaskResponse> {
    try {
      const response: AxiosResponse<any> = await api.get(`/v1/kanban/tasks/${id}`);
      return {
        task: response.data.task ? this.transformTask(response.data.task) : undefined,
        warning: response.data.warning,
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch Kanban task');
    }
  }

  static async createKanbanTask(data: ITaskCreateRequest): Promise<ITaskResponse> {
    try {
      const response: AxiosResponse<any> = await api.post('/v1/kanban/tasks', data);
      return {
        task: response.data.task ? this.transformTask(response.data.task) : undefined,
        warning: response.data.warning,
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create Kanban task');
    }
  }

  static async updateKanbanTask(id: string, data: ITaskUpdateRequest): Promise<ITaskResponse> {
    try {
      const response: AxiosResponse<any> = await api.put(`/v1/kanban/tasks/${id}`, data);
      return {
        task: response.data.task ? this.transformTask(response.data.task) : undefined,
        warning: response.data.warning,
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update Kanban task');
    }
  }

  static async deleteKanbanTask(id: string): Promise<void> {
    try {
      await api.delete(`/v1/kanban/tasks/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete Kanban task');
    }
  }

  static async moveKanbanTask(id: string, data: ITaskMoveRequest): Promise<ITaskResponse> {
    try {
      const response: AxiosResponse<any> = await api.put(`/v1/kanban/tasks/${id}/move`, data);
      return {
        task: response.data.task ? this.transformTask(response.data.task) : undefined,
        warning: response.data.warning,
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to move Kanban task');
    }
  }

  static async reorderKanbanTasks(data: ITaskReorderRequest): Promise<{ status: string }> {
    try {
      const response: AxiosResponse<{ status: string }> = await api.put('/v1/kanban/tasks/reorder', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to reorder Kanban tasks');
    }
  }

  // ============================================================================
  // Kanban Task Files (Google Drive)
  // ============================================================================

  static async getTaskFiles(taskId: string): Promise<IFileListResponse> {
    try {
      const response: AxiosResponse<IFileListResponse> = await api.get(`/v1/kanban/tasks/${taskId}/files`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch task files');
    }
  }

  static async uploadTaskFile(taskId: string, file: File): Promise<IFileUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response: AxiosResponse<IFileUploadResponse> = await api.post(
        `/v1/kanban/tasks/${taskId}/files`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to upload file');
    }
  }

  static async deleteTaskFile(taskId: string, fileId: string): Promise<void> {
    try {
      await api.delete(`/v1/kanban/tasks/${taskId}/files/${fileId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete file');
    }
  }

  // ============================================================================
  // Voice Messaging (WebRTC)
  // ============================================================================

  static async getOnlineUsers(): Promise<IOnlineUser[]> {
    try {
      const response: AxiosResponse<IOnlineUser[]> = await api.get('/v1/webrtc/online-users');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch online users');
    }
  }

  static async getVoiceMessages(): Promise<IStoredVoiceMessage[]> {
    try {
      const response: AxiosResponse<IStoredVoiceMessage[]> = await api.get('/v1/voice/messages');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch voice messages');
    }
  }

  static async storeVoiceMessage(data: IVoiceMessageRequest): Promise<IStoredVoiceMessage> {
    try {
      const response: AxiosResponse<IStoredVoiceMessage> = await api.post('/v1/voice/messages', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to store voice message');
    }
  }

  static async deleteVoiceMessage(messageId: string): Promise<void> {
    try {
      await api.delete(`/v1/voice/messages/${messageId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete voice message');
    }
  }

  static async markVoiceMessagePlayed(messageId: string): Promise<void> {
    try {
      await api.post(`/v1/voice/messages/${messageId}/played`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to mark message as played');
    }
  }

  static async getVoiceMessageCount(): Promise<IPendingCountResponse> {
    try {
      const response: AxiosResponse<IPendingCountResponse> = await api.get('/v1/voice/messages/count');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch message count');
    }
  }

  // Helper to get WebSocket URL for signaling
  static getWebSocketUrl(): string {
    const token = localStorage.getItem('auth_token');
    const baseUrl = getEnv('VITE_API_URL') || '';

    // Convert HTTP URL to WebSocket URL
    let wsBase = baseUrl.replace(/^http/, 'ws');

    // If no base URL, use current host
    if (!wsBase) {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsBase = `${protocol}//${window.location.host}`;
    }

    // Remove /api suffix if present
    wsBase = wsBase.replace(/\/api$/, '');

    return `${wsBase}/ws/signal?token=${token}`;
  }

  // ============================================================================
  // DigiKey API Integration
  // ============================================================================

  static async getDigiKeyAuthStatus(): Promise<{
    authenticated: boolean;
    expires_at?: string;
    updated_by?: string;
  }> {
    try {
      const response = await api.get('/v1/digikey/auth/status');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to get DigiKey auth status');
    }
  }

  static async initiateDigiKeyAuth(): Promise<{
    auth_url: string;
    state: string;
  }> {
    try {
      const response = await api.get('/v1/digikey/auth/init');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to initiate DigiKey auth');
    }
  }

  static async revokeDigiKeyAuth(): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const response = await api.post('/v1/digikey/auth/revoke');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to revoke DigiKey auth');
    }
  }

  // ============================================================================
  // Project Management
  // ============================================================================

  static async getProjects(): Promise<IProjectListResponse> {
    try {
      const response: AxiosResponse<IProjectListResponse> = await api.get('/v1/projects');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch projects');
    }
  }

  static async getProject(id: string): Promise<IProjectDetailResponse> {
    try {
      const response: AxiosResponse<IProjectDetailResponse> = await api.get(`/v1/projects/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch project');
    }
  }

  static async createProject(data: IProjectCreateRequest): Promise<IProject> {
    try {
      const response: AxiosResponse<{ project: IProject }> = await api.post('/v1/projects', data);
      return response.data.project;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create project');
    }
  }

  static async updateProject(id: string, data: IProjectUpdateRequest): Promise<IProject> {
    try {
      const response: AxiosResponse<{ project: IProject }> = await api.put(`/v1/projects/${id}`, data);
      return response.data.project;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update project');
    }
  }

  static async deleteProject(id: string): Promise<void> {
    try {
      await api.delete(`/v1/projects/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete project');
    }
  }

  // Project Phases
  static async getProjectPhases(projectId: string): Promise<IPhaseListResponse> {
    try {
      const response: AxiosResponse<IPhaseListResponse> = await api.get(
        `/v1/projects/${projectId}/phases`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch project phases');
    }
  }

  static async createProjectPhase(projectId: string, data: IPhaseCreateRequest): Promise<IProjectPhase> {
    try {
      const response: AxiosResponse<{ phase: IProjectPhase }> = await api.post(
        `/v1/projects/${projectId}/phases`,
        data
      );
      return response.data.phase;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create project phase');
    }
  }

  static async updateProjectPhase(
    projectId: string,
    phaseId: string,
    data: IPhaseUpdateRequest
  ): Promise<IProjectPhase> {
    try {
      const response: AxiosResponse<{ phase: IProjectPhase }> = await api.put(
        `/v1/projects/${projectId}/phases/${phaseId}`,
        data
      );
      return response.data.phase;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update project phase');
    }
  }

  static async deleteProjectPhase(projectId: string, phaseId: string): Promise<void> {
    try {
      await api.delete(`/v1/projects/${projectId}/phases/${phaseId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete project phase');
    }
  }

  static async reorderProjectPhases(projectId: string, data: IReorderRequest): Promise<void> {
    try {
      await api.put(`/v1/projects/${projectId}/phases/reorder`, data);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to reorder project phases');
    }
  }

  // Project Tasks
  static async getProjectTasks(projectId: string): Promise<IProjectTaskListResponse> {
    try {
      const response: AxiosResponse<IProjectTaskListResponse> = await api.get(
        `/v1/projects/${projectId}/tasks`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch project tasks');
    }
  }

  static async getProjectTasksByPhase(projectId: string, phaseId: string): Promise<IProjectTaskListResponse> {
    try {
      const response: AxiosResponse<IProjectTaskListResponse> = await api.get(
        `/v1/projects/${projectId}/phases/${phaseId}/tasks`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch phase tasks');
    }
  }

  static async createProjectTask(projectId: string, data: IProjectTaskCreateRequest): Promise<IProjectTask> {
    try {
      const response: AxiosResponse<{ task: IProjectTask }> = await api.post(
        `/v1/projects/${projectId}/tasks`,
        data
      );
      return response.data.task;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create project task');
    }
  }

  static async updateProjectTask(
    projectId: string,
    taskId: string,
    data: IProjectTaskUpdateRequest
  ): Promise<IProjectTask> {
    try {
      const response: AxiosResponse<{ task: IProjectTask }> = await api.put(
        `/v1/projects/${projectId}/tasks/${taskId}`,
        data
      );
      return response.data.task;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update project task');
    }
  }

  static async deleteProjectTask(projectId: string, taskId: string): Promise<void> {
    try {
      await api.delete(`/v1/projects/${projectId}/tasks/${taskId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete project task');
    }
  }

  static async moveProjectTask(
    projectId: string,
    taskId: string,
    data: IProjectTaskMoveRequest
  ): Promise<{ task: IProjectTask; warning?: string }> {
    try {
      const response: AxiosResponse<{ task: IProjectTask; warning?: string }> = await api.put(
        `/v1/projects/${projectId}/tasks/${taskId}/move`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to move project task');
    }
  }

  static async reorderProjectTasks(
    projectId: string,
    phaseId: string,
    data: IReorderRequest
  ): Promise<void> {
    try {
      await api.put(`/v1/projects/${projectId}/phases/${phaseId}/tasks/reorder`, data);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to reorder project tasks');
    }
  }

  static async checkProjectTaskDependencies(
    projectId: string,
    taskId: string
  ): Promise<IDependencyCheckResponse> {
    try {
      const response: AxiosResponse<IDependencyCheckResponse> = await api.get(
        `/v1/projects/${projectId}/tasks/${taskId}/dependencies`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to check task dependencies');
    }
  }
}

export default ApiService;